import { useDropzone } from 'react-dropzone';
import { PreviewImage } from '@components/PreviewImage';
import type React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { Button, buttonStyles, cn } from '@skupreme/shared/ui';
import { File, Trash } from '@phosphor-icons/react';
import { CommonUtility } from '@utility/common';
import type {
  FileItem,
  FileUploadComponentProps,
} from 'types/components/file-upload';

export function FileUploadComponent({
  uploadText,
  maxFileSize,
  accept,
  containerHeight = 300,
  maxFiles = 1,
  files,
  setFiles,
  instructions = [],
  hideIcon = false,
}: FileUploadComponentProps) {
  const hasWindow = typeof window !== 'undefined';
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLDivElement>(null);

  const addNewFiles = (newFiles: File[]) => {
    if (
      (maxFiles || 0) === 1 &&
      newFiles.length > 0 &&
      (!maxFileSize || newFiles[0].size / 1048576 <= maxFileSize)
    ) {
      setFiles([
        {
          name: newFiles[0].name,
          file: newFiles[0],
        },
      ]);
      (fileInputRef.current!.getElementsByTagName('input')[0] as any).value =
        null;
      return;
    }
    const temp: FileItem[] = [];
    for (const file of newFiles) {
      if (!maxFileSize || file.size / 1048576 <= maxFileSize) {
        if (!maxFiles || files.length < maxFiles) {
          temp.push({
            name: file.name,
            file,
          });
        }
      }
    }
    setFiles(x => [...x, ...temp]);
    (fileInputRef.current!.getElementsByTagName('input')[0] as any).value =
      null;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    addNewFiles(acceptedFiles);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept,
    maxFiles,
    multiple: !maxFiles || maxFiles > 1,
    onDrop,
  });

  const removeFile = (fileName: string) => {
    setFiles(files.filter(x => x.name !== fileName));
  };

  const closePreviewImage = () => {
    setPreviewUrl('');
    setShowPreview(false);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {};
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [hasWindow]);

  return (
    <>
      {CommonUtility.isValidArray(instructions) && (
        <div
          className={cn(
            "sk:mt-5 sk:font-['Nunito_Sans'] sk:font-normal sk:text-fg",
            'sk:[&>div]:mb-2 sk:[&>div]:flex sk:[&>div]:items-center',
            'sk:[&_.download-link_button]:flex! sk:[&_.download-link_button]:items-center! sk:[&_.download-link_button]:justify-center! sk:[&_.download-link_button]:px-[15px] sk:[&_.download-link_button]:py-[9px] sk:[&_.download-link_button]:text-xs sk:[&_.download-link_button]:font-bold!',
          )}
        >
          {instructions.map(({ id, label }) => (
            <div key={id}>
              {instructions.length > 1 && (
                <span className="sk:mr-2 sk:ml-1 sk:text-base sk:text-fg-tertiary">
                  {id}
                </span>
              )}{' '}
              {label}
            </div>
          ))}
        </div>
      )}
      {files.length < (maxFiles || 0) && (
        <div
          className={cn(
            'sk:flex sk:flex-1 sk:cursor-pointer sk:flex-col sk:items-center sk:justify-center sk:px-12 sk:py-[1.6rem]',
            'sk:rounded-[5px] sk:border sk:border-dashed sk:border-border sk:bg-fill-tertiary sk:text-fg-tertiary sk:outline-none',
            'sk:transition-[border] sk:duration-200',
            'sk:[&_p]:text-sm sk:[&_p]:leading-[18px] sk:[&_p]:text-[#262626]',
          )}
          style={{ height: containerHeight }}
          {...(getRootProps({
            isDragActive,
            isDragAccept,
            isDragReject,
          } as any) as any)}
          ref={fileInputRef}
        >
          <input {...(getInputProps() as any)} />
          {!hideIcon && (
            <img src="/images/icon-cloud-upload.svg" alt="Upload" />
          )}
          {uploadText && (
            <p className="sk:mt-2! sk:mb-0! sk:text-center">{uploadText}</p>
          )}
          <p className="sk:mt-2!">Drag and drop here, or</p>
          {/* Text in a button's look: the whole drop area is the control. */}
          <span
            className={buttonStyles({ size: 'lg', className: 'sk:font-bold' })}
          >
            Browse
          </span>
        </div>
      )}
      <article
        className="sk:flex sk:flex-wrap sk:-mx-4 sk:mx-0! sk:w-full sk:mb-[15px]"
        style={{ height: containerHeight }}
      >
        {files.map(file => (
          <div
            className="sk:w-full sk:min-w-0 sk:flex-1 sk:px-4 sk:px-0! sk:mx-0! sk:my-2! sk:relative sk:flex sk:items-center sk:rounded-[5px] sk:border sk:border-solid sk:border-rule"
            key={file.name}
          >
            <File size={32} className="sk:ml-3.5! icon" />
            <p className="sk:m-0! sk:mx-5! sk:w-full sk:truncate">
              {file.name}
            </p>
            <Button
              variant="ghost"
              shape="circle"
              aria-label={`Remove ${file.name}`}
              onPress={() => removeFile(file.name)}
              className="sk:mr-1"
            >
              <Trash size={18} className="icon" />
            </Button>
          </div>
        ))}
      </article>
      <PreviewImage
        open={showPreview}
        url={previewUrl}
        closeModal={closePreviewImage}
      />
    </>
  );
}
